import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import '../styles/pricing.css';

// Marks that a user has seen Edlyn's welcome note so it auto-opens only once.
const INFO_SEEN_KEY = 'aidify_pro_info_seen';

// Institutional pricing enquiry form.
const INSTITUTIONAL_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSd97scAYTOQHFUWITnTxIMDIw3Zfys02HJaZeqbU3WTUFiS-Q/viewform';

const Pricing = () => {
  const {
    isProUser,
    plan,
    reportCount,
    freeLimit,
    resetDate,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    cancelAt,
    hasStripeCustomer,
    startCheckout,
    switchPlan,
    cancelSubscription,
    resumeSubscription,
    openPortal,
  } = UserAuth();
  const { lang } = useLanguage();
  const T = translations[lang].pricing;

  const formatDate = (date) =>
    date?.toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });

  const resetDateLabel = formatDate(resetDate);
  const periodEndLabel = formatDate(currentPeriodEnd);
  // Stripe's cancel_at can precede the period end, so it wins when set.
  const accessUntilLabel = formatDate(cancelAt || currentPeriodEnd);

  const [searchParams] = useSearchParams();
  const status = searchParams.get('status'); // 'success' | 'cancel'
  const [busy, setBusy] = useState(null); // 'monthly' | 'yearly' | 'portal'
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [showInfo, setShowInfo] = useState(false);

  // First visit: auto-open the note once, then remember it was shown.
  useEffect(() => {
    try {
      if (!localStorage.getItem(INFO_SEEN_KEY)) {
        setShowInfo(true);
        localStorage.setItem(INFO_SEEN_KEY, '1');
      }
    } catch {
      // localStorage unavailable (private mode, etc.) — just skip auto-open.
    }
  }, []);

  // Let Escape close the info modal.
  useEffect(() => {
    if (!showInfo) return;
    const onKey = (e) => e.key === 'Escape' && setShowInfo(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showInfo]);

  const handleSubscribe = async (planName) => {
    setError(null);
    setBusy(planName);
    try {
      await startCheckout(planName, T.redirecting);
    } catch (err) {
      console.error(err);
      // Backend refuses a second subscription when one is already active — route
      // the user to the portal to switch plans instead of a new checkout.
      const alreadySubscribed =
        err?.code === 'functions/failed-precondition' ||
        err?.details?.code === 'already-subscribed';
      if (alreadySubscribed) {
        setError(T.alreadySubscribedMsg);
      } else if (err?.code === 'unauthenticated' || err?.code === 'functions/unauthenticated') {
        setError(T.loginRequired);
      } else {
        setError(T.errorMsg);
      }
    } finally {
      // Checkout opens in a new tab now, so this tab survives and the buttons
      // must be re-enabled on success too, not just on error.
      setBusy(null);
    }
  };

  const handlePortal = async () => {
    setError(null);
    setBusy('portal');
    try {
      await openPortal(T.redirecting);
    } catch (err) {
      console.error(err);
      setError(T.errorMsg);
    } finally {
      setBusy(null);
    }
  };

  // In-app plan switch (monthly <-> yearly) with immediate proration. No tab
  // hop — the live users/{uid} snapshot updates `plan` once Stripe returns.
  const handleSwitch = async (planName, planLabel) => {
    setError(null);
    setNotice(null);
    if (!window.confirm(T.confirmSwitch(planLabel))) return;
    setBusy(planName);
    try {
      await switchPlan(planName);
      setNotice(T.switchSuccess);
    } catch (err) {
      console.error(err);
      setError(T.errorMsg);
    } finally {
      setBusy(null);
    }
  };

  // Graceful cancel at period end (reversible). No tab hop — the users/{uid}
  // snapshot flips cancelAtPeriodEnd/cancelAt once Stripe returns.
  const handleCancel = async () => {
    setError(null);
    setNotice(null);
    if (!window.confirm(T.confirmCancel(accessUntilLabel || periodEndLabel))) return;
    setBusy('cancel');
    try {
      await cancelSubscription();
      // No success notice — the persistent "Subscription cancelled" banner
      // (shown whenever cancelAtPeriodEnd is set) already confirms it.
    } catch (err) {
      console.error(err);
      setError(T.errorMsg);
    } finally {
      setBusy(null);
    }
  };

  const handleResume = async () => {
    setError(null);
    setNotice(null);
    if (!window.confirm(T.confirmResume)) return;
    setBusy('resume');
    try {
      await resumeSubscription();
      setNotice(T.resumeSuccess);
    } catch (err) {
      console.error(err);
      setError(T.errorMsg);
    } finally {
      setBusy(null);
    }
  };

  const Plan = ({ planName, name, price, period, note }) => {
    const isCurrent = isProUser && plan === planName;
    return (
      <div className={`pricing-card${isCurrent ? ' pricing-card--current' : ''}`}>
        <h2 className="pricing-card-name">{name}</h2>
        <div className="pricing-card-price">
          <span className="pricing-card-amount">{price}</span>
          <span className="pricing-card-period">{period}</span>
        </div>
        {note && <p className="pricing-card-note">{note}</p>}
        <ul className="pricing-card-features">
          <li>{T.unlimitedReports}</li>
          <li>{T.everythingFree}</li>
          <li>{T.cancelAnytime}</li>
        </ul>
        {isCurrent ? (
          <button className="pricing-btn pricing-btn--current" disabled>
            {T.currentPlan}
          </button>
        ) : isProUser ? (
          // Already subscribed to the other plan — switch the existing
          // subscription in place (immediate proration), never a 2nd checkout.
          <button
            className="pricing-btn pricing-btn--subscribe"
            onClick={() => handleSwitch(planName, name)}
            disabled={busy !== null}
          >
            {T.switchPlan}
          </button>
        ) : (
          <button
            className="pricing-btn pricing-btn--subscribe"
            onClick={() => handleSubscribe(planName)}
            disabled={busy !== null}
          >
            {T.subscribe}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="pricing-page">
      <h1 className="pricing-title">
        {T.title}
        <button
          type="button"
          className="pricing-info-btn"
          aria-label={T.info.ariaLabel}
          onClick={() => setShowInfo(true)}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2" />
            <line x1="12" y1="11" x2="12" y2="16.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="7.75" r="1.25" fill="currentColor" />
          </svg>
        </button>
      </h1>
      {/* The subtitle only pitches the free tier, so it's irrelevant once subscribed. */}
      {!isProUser && <p className="pricing-subtitle">{T.subtitle}</p>}

      {showInfo && (
        <div
          className="pricing-info-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={T.info.ariaLabel}
          onClick={() => setShowInfo(false)}
        >
          <div className="pricing-info-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="pricing-info-close"
              aria-label={T.info.close}
              onClick={() => setShowInfo(false)}
            >
              ×
            </button>
            <p>{T.info.greeting}</p>
            <p>
              {T.info.body1Before}
              <a href={`mailto:${T.info.email}`}>{T.info.email}</a>
              {T.info.body1After}
            </p>
            <p>{T.info.body2}</p>
            <p className="pricing-info-signoff">
              {T.info.closing}
              <br />
              {T.info.signature}
            </p>
          </div>
        </div>
      )}

      {status === 'success' && <div className="pricing-banner pricing-banner--success">{T.successMsg}</div>}
      {status === 'cancel' && <div className="pricing-banner pricing-banner--cancel">{T.cancelMsg}</div>}
      {notice && <div className="pricing-banner pricing-banner--success">{notice}</div>}
      {error && <div className="pricing-banner pricing-banner--error">{error}</div>}
      {isProUser && cancelAtPeriodEnd && (
        <div className="pricing-banner pricing-banner--cancel">
          {T.subscriptionCancelled(accessUntilLabel)}
        </div>
      )}

      {isProUser ? (
        <>
          <p className="pricing-status">{T.proActive}</p>
          {currentPeriodEnd && !cancelAtPeriodEnd && (
            <p className="pricing-status-billing">
              {T.nextPayment(periodEndLabel)}
            </p>
          )}
        </>
      ) : (
        <p className="pricing-status">
          {T.freeUsage(reportCount, freeLimit)}{' '}
          <span className="pricing-status-reset">{T.resetsOn(resetDateLabel)}</span>
        </p>
      )}

      <div className="pricing-cards">
        <Plan
          planName="monthly"
          name={T.monthlyName}
          price={T.monthlyPrice}
          period={T.monthlyPeriod}
        />
        <Plan
          planName="yearly"
          name={T.yearlyName}
          price={T.yearlyPrice}
          period={T.yearlyPeriod}
          note={T.yearlyNote}
        />
      </div>

      {isProUser && (
        cancelAtPeriodEnd ? (
          <button
            className="pricing-btn pricing-btn--resume"
            onClick={handleResume}
            disabled={busy !== null}
          >
            {T.resumeSubscription}
          </button>
        ) : (
          <button
            className="pricing-btn pricing-btn--cancel"
            onClick={handleCancel}
            disabled={busy !== null}
          >
            {T.cancelSubscription}
          </button>
        )
      )}

      {(isProUser || hasStripeCustomer) && (
        <button
          className="pricing-btn pricing-btn--portal"
          onClick={handlePortal}
          disabled={busy !== null}
        >
          {T.manageSubscription}
        </button>
      )}

      <p className="pricing-institutional">
        {T.institutionalBefore}
        <a href={`mailto:${T.institutionalEmail}`}>{T.institutionalEmail}</a>
        {T.institutionalOr}
        <a href={INSTITUTIONAL_FORM_URL} target="_blank" rel="noopener noreferrer">
          {T.institutionalFormLabel}
        </a>
        {T.institutionalAfter}
      </p>
    </div>
  );
};

export default Pricing;
